import { createChallenge, createState, createVerifier } from './pkce.js';
import { clearOidcDebug, clearOidcRequest, clearSession, decodeJwtPayload, loadOidcDebug, loadOidcRequest, loadSession, markLogout, requestOidcReauthorization, saveOidcDebug, saveOidcRequest, saveSession } from './state.js';
import { getOidcConfig, oidcPostLogoutRedirectUri, oidcRedirectUri } from './config.js';
import { getIamAppKey, getIamLoginBaseUrl } from './portal.js';

function isDebugEnabled() {
  try {
    const q = new URLSearchParams(window.location.search);
    if (q.get('debug') === '1') return true;
  } catch {
    // ignore
  }
  try {
    return localStorage.getItem('chamii:oidc:debug') === '1';
  } catch {
    return false;
  }
}

function redactToken(token, keep = 6) {
  const s = String(token || '');
  if (!s) return '';
  if (s.length <= keep) return `${s}…`;
  return `${s.slice(0, keep)}…(${s.length})`;
}

const TOKEN_EXCHANGE_TIMEOUT_MS = 15_000;

export async function exchangeTokenRequest(tokenUrl, body, timeoutMs = TOKEN_EXCHANGE_TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(tokenUrl, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body,
      signal: controller.signal,
    });
  } catch (error) {
    if (controller.signal.aborted || error?.name === 'AbortError') throw new Error('token_exchange_timeout');
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

function oidcBaseOrigin(cfg) {
  const raw = String(cfg?.baseUrl || '').trim();
  let u = null;
  try {
    u = new URL(raw);
  } catch {
    throw new Error('invalid_oidc_base_url');
  }
  if (u.username || u.password || u.search || u.hash) throw new Error('invalid_oidc_base_url');
  if (u.pathname && u.pathname !== '/') throw new Error('invalid_oidc_base_url');

  // For Chamii IAM provider, if a portal origin is provided, redirect to the OIDC backend origin.
  if (cfg?.provider === 'iam') {
    const host = String(u.hostname || '').toLowerCase();
    if (host === 'iam.chamii.com') {
      return 'https://iam-api.chamii.com';
    }
    if (host === 'iam-dev.chamii.com') {
      return 'https://iam-backend-dev.chamii.com';
    }
    if (host === 'iam-api-dev.chamii.com') {
      return 'https://iam-backend-dev.chamii.com';
    }
    if (host === 'iam-test.chamii.com') {
      return 'https://iam-backend-test.chamii.com';
    }
  }

  return u.origin;
}

export async function startLogin(next) {
  const cfg = getOidcConfig();
  if (!cfg) throw new Error('missing_oidc_config');

  const base = oidcBaseOrigin(cfg);
  const state = createState();
  const verifier = createVerifier();
  const challenge = await createChallenge(verifier);

  saveOidcRequest({ state, verifier, next });

  const url =
    cfg.provider === 'keycloak'
      ? new URL(`/realms/${encodeURIComponent(cfg.realm || '')}/protocol/openid-connect/auth`, base)
      : new URL('/oauth/authorize', base);
  url.searchParams.set('client_id', cfg.clientId);
  url.searchParams.set('redirect_uri', oidcRedirectUri());
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', 'openid profile email');
  url.searchParams.set('state', state);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('code_challenge', challenge);

  if (isDebugEnabled()) {
    saveOidcDebug({
      at: Date.now(),
      phase: 'startLogin',
      provider: cfg.provider,
      baseUrl: cfg.baseUrl,
      clientId: cfg.clientId,
      realm: cfg.realm || undefined,
      next: next || '/',
      redirectUri: oidcRedirectUri(),
      authUrl: url.toString(),
      state: redactToken(state),
    });
  } else {
    clearOidcDebug();
  }

  if (cfg.provider === 'iam') {
    try {
      const portalBase = getIamLoginBaseUrl();
      const appKey = getIamAppKey();
      const portal = new URL(`${portalBase}/${encodeURIComponent(appKey)}/`);
      portal.searchParams.set('next', url.toString());
      window.location.assign(portal.toString());
      return;
    } catch {
      // Fall back to direct /oauth/authorize if portal URL construction fails.
    }
  }

  window.location.assign(url.toString());
}

export async function finishLogin(code, state) {
  const cfg = getOidcConfig();
  if (!cfg) throw new Error('missing_oidc_config');

  const base = oidcBaseOrigin(cfg);
  const req = loadOidcRequest();
  if (!req) throw new Error('missing_oidc_request');
  if (req.state !== state) throw new Error('state_mismatch');

  const tokenUrl =
    cfg.provider === 'keycloak'
      ? new URL(`/realms/${encodeURIComponent(cfg.realm || '')}/protocol/openid-connect/token`, base)
      : new URL('/oauth/token', base);

  const body = new URLSearchParams();
  body.set('grant_type', 'authorization_code');
  body.set('client_id', cfg.clientId);
  body.set('redirect_uri', oidcRedirectUri());
  body.set('code', code);
  body.set('code_verifier', req.verifier);

  if (isDebugEnabled()) {
    const prev = loadOidcDebug() || {};
    saveOidcDebug({
      ...prev,
      at: Date.now(),
      phase: 'tokenExchange',
      tokenUrl: tokenUrl.toString(),
      callback: {
        code: redactToken(code),
        state: redactToken(state),
      },
    });
  }

  let resp;
  try {
    resp = await exchangeTokenRequest(tokenUrl, body);
  } catch (e) {
    if (isDebugEnabled()) {
      const prev = loadOidcDebug() || {};
      saveOidcDebug({
        ...prev,
        at: Date.now(),
        phase: 'tokenExchange',
        tokenError: String(e?.message || e),
      });
    }
    if (String(e?.message || e) === 'token_exchange_timeout') throw e;
    throw new Error(`token_exchange_failed: fetch_error ${String(e?.message || e)}`);
  }

  const text = await resp.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = { raw: text };
  }
  if (!resp.ok) throw new Error(`token_exchange_failed: ${resp.status} ${JSON.stringify(json)}`);

  clearOidcRequest();

  const session = {
    tokens: {
      accessToken: String(json?.access_token || ''),
      idToken: json?.id_token ? String(json.id_token) : undefined,
      expiresIn: typeof json?.expires_in === 'number' ? json.expires_in : undefined
    },
    obtainedAt: Date.now(),
    user: json?.id_token ? decodeJwtPayload(String(json.id_token || '')) : undefined
  };
  if (!session.tokens.accessToken) throw new Error('missing_access_token');

  if (isDebugEnabled()) {
    const prev = loadOidcDebug() || {};
    saveOidcDebug({
      ...prev,
      at: Date.now(),
      phase: 'ok',
      tokenResult: {
        ok: true,
        hasAccessToken: Boolean(session.tokens.accessToken),
        hasIdToken: Boolean(session.tokens.idToken),
        expiresIn: session.tokens.expiresIn,
      },
    });
  }

  saveSession(session);
  return { next: req.next || '/' };
}

export function buildLogoutUrl() {
  const cfg = getOidcConfig();
  if (!cfg) return null;
  let base = null;
  try {
    base = oidcBaseOrigin(cfg);
  } catch {
    return null;
  }
  const session = loadSession();
  if (!session) return null;
  const idToken = session?.tokens?.idToken ? String(session.tokens.idToken) : null;

  clearSession();
  clearOidcRequest();

  if (cfg.provider === 'keycloak') {
    const url = new URL(`/realms/${encodeURIComponent(cfg.realm || '')}/protocol/openid-connect/logout`, base);
    if (idToken) url.searchParams.set('id_token_hint', idToken);
    url.searchParams.set('post_logout_redirect_uri', oidcPostLogoutRedirectUri());
    return url.toString();
  }

  const loginAfterLogout = new URL('/login', window.location.origin);
  loginAfterLogout.searchParams.set('fresh', '1');
  loginAfterLogout.searchParams.set('auto', '0');

  const url = new URL('/logout', base);
  url.searchParams.set('next', loginAfterLogout.toString());
  return url.toString();
}

export function logoutLocalOnly() {
  clearSession();
  clearOidcRequest();
}

export function reauthorizeAfterUnauthorized(next) {
  if (!requestOidcReauthorization()) return false;
  clearSession();
  const params = new URLSearchParams({
    next: next || `${window.location.pathname}${window.location.search}` || '/',
    auto: 'oidc',
    reauth: '1',
  });
  window.location.replace(`/login?${params.toString()}`);
  return true;
}

export function startLogout() {
  markLogout();
  const url = buildLogoutUrl();
  if (url) {
    // Prefer `replace` so back button won't land on an authenticated console page.
    // Fallback: if navigation is blocked for any reason, force a safe local landing
    // to avoid auto re-login loops.
    const t = setTimeout(() => {
      try {
        window.location.replace('/login?fresh=1&auto=0');
      } catch {
        // ignore
      }
    }, 2500);
    try {
      window.addEventListener('pagehide', () => clearTimeout(t), { once: true });
    } catch {
      // ignore
    }
    window.location.replace(url);
    return;
  }
  logoutLocalOnly();
  window.location.replace('/login?fresh=1&auto=0');
}
